// src/oura/newscan/branches.ts
//
// "Which branches?" on a First AI check (Jack, 19 Oct 2026: the outlet cards
// were "very rigid" and could not say "Lush is our name, the branches are
// Mont Kiara and Bukit Bintang"). The Review folds every card about which
// places are branches into one list:
//
//   form_outlet_missing   an outlet the form does not list
//   form_outlet_new       an area the form names that is not an outlet
//   outlet_undeclared     a name the site uses that is not an outlet
//   outlet_unverified     an outlet the site never names
//   outlets_vs_plan       the quote's count against the outlets
//   plan_outlet_names     the plan's outlet names (filled from the answer)
//
// `branchRows` is the list with Oura's best guess ticked and a reason beside
// each doubtful name. `nextBranchStep` reconciles the draft with the names
// staff confirmed, one existing write at a time (the cards' own options,
// then the outlets and Draft again, then the quote and the plan), so it
// works on today's API. Pure.

import type { DecisionChoice, ScanDraft, ScanDraftDecision, ScanDraftOutlet } from "@service/aeo/scanDraftTypes";
import { isFirstCheckDraft } from "@service/aeo/scanDraftTypes";
import { canonicalTown, townForArea } from "./towns";

export const BRANCH_CARD_TYPES: ReadonlyArray<ScanDraftDecision["type"]> = [
    "form_outlet_missing",
    "form_outlet_new",
    "outlet_undeclared",
    "outlet_unverified",
    "outlets_vs_plan",
    "plan_outlet_names",
];

export const isBranchCard = (d: ScanDraftDecision): boolean => BRANCH_CARD_TYPES.includes(d.type);

const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
const same = (a: string, b: string) => norm(a) === norm(b);
const words = (s: string) => norm(s).split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/** The open cards this list replaces. */
export function openBranchCards(draft: ScanDraft): ScanDraftDecision[] {
    return draft.decisions.filter((d) => d.state === "open" && isBranchCard(d));
}

/** Whether the Review shows the branches list: a First AI check of a business with a door. */
/** The branches card's question, also the Review section that holds it. */
export const branchesTitle = (name: string): string => `Which branches does ${name || "the business"} have?`;

export function showsBranches(draft: ScanDraft | null): boolean {
    return Boolean(draft && isFirstCheckDraft(draft) && draft.business.buy !== "online");
}

/** The place a card is about, read from its options. */
export function cardPlace(d: ScanDraftDecision): string | null {
    for (const o of [d.recommended, ...d.alternatives]) {
        const a = o?.action as { kind: string; name?: string; area?: string } | undefined;
        if (a && (a.kind === "add_outlet" || a.kind === "drop_outlet" || a.kind === "confirm_outlet") && a.name) return a.name;
        if (a && a.kind === "open_business" && a.area) return a.area;
    }
    // "Admin does not list Lush" with Keep as a plain record.
    const m = /does not list (.+)$/.exec(d.title) ?? /names an outlet in (.+)$/.exec(d.title);
    return m ? m[1].trim() : null;
}

/**
 * The outlets the 50 ask about now: every named outlet on the draft, whatever its source. An
 * unconfirmed outlet from the pitch, the form or the quote is still asked about, so it stays on the
 * branches list (its source says where it came from); leaving it out let Apply drop it (Jack, #4451).
 */
export function activeBranches(draft: ScanDraft): ScanDraftOutlet[] {
    return draft.business.outlets.filter((o) => o.name.trim().length > 0);
}

export interface BranchRow {
    name: string;
    /** Where the name comes from, in one line. */
    from: string;
    /** Oura's best guess. */
    guess: boolean;
    /** Why Oura doubts it, when it does. */
    doubt: string | null;
    /** Asked about in the 50 today. */
    active: boolean;
}

export interface BranchList {
    rows: BranchRow[];
    /** Names the site uses that are not places: folded, never asked. */
    skipped: string[];
    /** The quote's outlet count. Null: no quote on the record. */
    quote: number | null;
}

const SOURCE: Record<ScanDraftOutlet["source"], string> = {
    site: "On the website",
    form: "In the form",
    scan: "In the pitch",
    quote: "On the quote",
    staff: "Added by staff",
};

/** The business name's own words, the generic ones left out ("The Lush Clinic" gives "lush"). */
function nameWords(draft: ScanDraft): string[] {
    const generic = new Set(["the", "and", "of", "sdn", "bhd", "clinic", "centre", "center", "studio", "group", ...words(draft.business.category)]);
    return words(draft.business.businessName).filter((w) => !generic.has(w));
}

/** "Part of the business name": every word of the place is one of the name's own words. */
function inBusinessName(place: string, draft: ScanDraft): boolean {
    const own = nameWords(draft);
    const w = words(place).filter((x) => !["clinic", "centre", "center", "studio"].includes(x));
    return own.length > 0 && w.length > 0 && w.every((x) => own.includes(x));
}

/** A name Oura can place on the map: a town, a known sub-area, or an area the form or quote names. */
function knownPlace(name: string, named: string[]): boolean {
    return townForArea(name) !== null || named.some((n) => same(n, name));
}

/** The list, Oura's guess ticked. */
export function branchRows(draft: ScanDraft): BranchList {
    const cards = draft.decisions.filter((d) => isBranchCard(d) && d.state === "open");
    const notInForm = new Set(cards.filter((d) => d.type === "form_outlet_missing").map((d) => norm(cardPlace(d) ?? "")));
    const formNew = cards.filter((d) => d.type === "form_outlet_new").map((d) => cardPlace(d)).filter((x): x is string => Boolean(x));
    const siteNew = cards.filter((d) => d.type === "outlet_undeclared").map((d) => cardPlace(d)).filter((x): x is string => Boolean(x));
    const unverified = new Set(cards.filter((d) => d.type === "outlet_unverified").map((d) => norm(cardPlace(d) ?? "")));
    const quote = draft.firstCheck?.quote?.outlets ?? null;
    const planNames = draft.firstCheck?.planOutlets ?? [];

    const active = activeBranches(draft);
    const candidates: Array<{ name: string; from: string; active: boolean }> = [
        ...active.map((o) => {
            const notes = [SOURCE[o.source] ?? "In the draft"];
            if (notInForm.has(norm(o.name))) notes.push("not in the form");
            if (unverified.has(norm(o.name))) notes.push("not on the website");
            return { name: o.name, from: notes.join(", "), active: true };
        }),
        ...formNew.filter((n) => !active.some((o) => same(o.name, n))).map((name) => ({ name, from: "In the form", active: false })),
    ];
    const named = [...candidates.map((c) => c.name), ...planNames];
    const places = siteNew.filter((n) => !candidates.some((c) => same(c.name, n)) && knownPlace(n, named) && !inBusinessName(n, draft));
    const skipped = siteNew.filter((n) => !candidates.some((c) => same(c.name, n)) && !places.some((p) => same(p, n)));
    candidates.push(...places.map((name) => ({ name, from: "On the website", active: false })));

    const towns = new Map(candidates.map((c) => [c.name, townForArea(c.name)]));
    const rows = candidates.map((c): BranchRow => {
        const own = canonicalTown(c.name);
        let doubt: string | null = null;
        if (inBusinessName(c.name, draft)) doubt = "Part of the business name, not a place.";
        else if (own) {
            const inside = candidates.filter((o) => o.name !== c.name && !canonicalTown(o.name) && towns.get(o.name) === own).map((o) => o.name);
            doubt = inside.length
                ? `A city. ${inside.slice(0, 2).join(" and ")} ${inside.length === 1 ? "is" : "are"} in it.`
                : "A city, not a branch's area. Name the part of it the branch is in.";
        } else if (!c.active && c.from === "On the website") doubt = "The website names it. Tick it only if customers can walk in.";
        const guess = doubt === null && (c.active || c.from === "In the form");
        return { name: c.name, from: c.from, guess, doubt, active: c.active };
    });
    return { rows, skipped, quote };
}

/**
 * The questions that ask about a word of the business name as if it were a
 * branch ("Best aesthetic clinic in Lush?"), one group per name. Each comes
 * in as its own "contains the business name" card, and the reword it offers
 * still names the place, so it fixes nothing: what fixes them is leaving the
 * name out on the branches list, which moves the questions to the real
 * branches. The Review shows the group as one card that points there.
 *
 * `folded`: other open cards that only concern these questions (two of them
 * asking the same thing). They go when the name does, so they wait with it.
 */
export interface PlaceNameGroup {
    place: string;
    cards: ScanDraftDecision[];
    folded: ScanDraftDecision[];
    /** Question numbers, in order. */
    slots: number[];
}

export function placeNameGroups(draft: ScanDraft): PlaceNameGroup[] {
    if (!showsBranches(draft)) return [];
    const open = draft.decisions.filter((d) => d.state === "open");
    const out: PlaceNameGroup[] = [];
    for (const row of branchRows(draft).rows) {
        if (!row.active || !inBusinessName(row.name, draft)) continue;
        const tail = `@${norm(row.name)}`;
        const cards = open.filter((d) => d.type === "lint_error" && norm(d.id).endsWith(tail));
        if (!cards.length) continue;
        const slots = Array.from(new Set(cards.flatMap((d) => d.slots))).sort((x, y) => x - y);
        const inGroup = new Set(slots);
        const folded = open.filter((d) => !cards.includes(d) && d.type === "duplicate" && d.slots.length > 0 && d.slots.every((n) => inGroup.has(n)));
        out.push({ place: row.name, cards, folded, slots });
    }
    return out;
}

/** The write that brings the draft one step closer to `target`, or null when it matches. */
export type BranchStep =
    | { kind: "answer"; decisionId: string; choice: DecisionChoice; label: string }
    | { kind: "outlets"; outlets: ScanDraftOutlet[] }
    | { kind: "rebuild" }
    | { kind: "plan"; decisionId: string; values: string[] };

function optionIndex(d: ScanDraftDecision, kinds: string[]): DecisionChoice | null {
    if (d.recommended && kinds.includes(d.recommended.action.kind)) return "recommended";
    const i = d.alternatives.findIndex((o) => kinds.includes(o.action.kind));
    return i >= 0 ? { alternative: i } : null;
}

const keepKinds = ["record", "confirm_outlet", "add_outlet", "ack"];
const dropKinds = ["drop_outlet", "dismiss"];

/**
 * The next write. Cards first (a town that is an outlet goes first, since a
 * town anchor refuses every other outlet write), then the outlets list for
 * what no card covers (with Draft again when that leaves the draft stale),
 * then the quote's count ("2 is right") and the plan's outlet names.
 */
export function nextBranchStep(draft: ScanDraft, target: string[]): BranchStep | null {
    const want = (n: string) => target.some((t) => same(t, n));
    const cards = draft.decisions.filter((d) => d.state === "open" && isBranchCard(d) && d.type !== "outlets_vs_plan" && d.type !== "plan_outlet_names");
    const ordered = [...cards].sort((a, b) => Number(!canonicalTown(cardPlace(a) ?? "")) - Number(!canonicalTown(cardPlace(b) ?? "")));
    for (const d of ordered) {
        const place = cardPlace(d);
        if (!place) continue;
        const choice = want(place) ? optionIndex(d, keepKinds) : optionIndex(d, dropKinds) ?? "dismiss";
        if (choice) return { kind: "answer", decisionId: d.id, choice, label: place };
    }

    const active = activeBranches(draft);
    const differs =
        active.length !== target.length || active.some((o) => !want(o.name)) || target.some((t) => !active.some((o) => same(o.name, t)));
    if (differs) {
        const outlets: ScanDraftOutlet[] = target.map((name) => {
            const had = draft.business.outlets.find((o) => same(o.name, name));
            return had
                ? { ...had, confirmed: true }
                : { name, town: townForArea(name) ?? draft.business.city ?? "", source: "staff", confirmed: true };
        });
        return { kind: "outlets", outlets };
    }
    if (draft.stale) return { kind: "rebuild" };

    const quote = draft.decisions.find((d) => d.state === "open" && d.type === "outlets_vs_plan");
    if (quote) {
        const choice = optionIndex(quote, ["record", "ack"]);
        if (choice) return { kind: "answer", decisionId: quote.id, choice, label: "quote" };
    }
    const plan = draft.decisions.find((d) => d.state === "open" && d.type === "plan_outlet_names");
    if (plan) return { kind: "plan", decisionId: plan.id, values: target };
    return null;
}

/** What Oura read from staff's own words: the branches to keep and the names to stop treating as branches. */
export interface BranchReading {
    keep: string[];
    drop: string[];
}

/**
 * Plain words to branches, on the screen until the backend reads them with a
 * model (handoff). Two shapes:
 *   - "the branches are Mont Kiara and Bukit Bintang" (or "outlets are", "we
 *     are in", "only"): the names after it are kept, every other one dropped;
 *   - "Lush is part of the name", "KL is not a branch", "drop Lush": those
 *     names are dropped and Oura's guess (`guess`) is kept for the rest.
 * Null when the sentence names no branch either way.
 */
export function readBranches(text: string, names: string[], guess: string[] = []): BranchReading | null {
    const t = ` ${norm(text)} `;
    const mentioned = (part: string) => names.filter((n) => ` ${part} `.includes(` ${norm(n)} `) || part.includes(norm(n)));
    const split = t.split(/\b(?:branch(?:es)?|outlets?|locations?|clinics?|stores?|shops?)\s+(?:are|is)\b|\bwe are (?:in|at)\b|\bonly\b/);
    if (split.length >= 2) {
        const after = split.slice(1).join(" ");
        const keep = mentioned(after);
        // A place typed that is not on the list yet: "branches are Mont Kiara and Cheras".
        const typed = after
            .split(/,|\band\b|\.|;/)
            .map((x) => x.trim())
            .filter((x) => x && townForArea(x) !== null && !names.some((n) => same(n, x)))
            .map((x) => x.replace(/\b\w/g, (c) => c.toUpperCase()));
        const all = [...keep, ...typed];
        if (all.length) return { keep: all, drop: names.filter((n) => !all.some((k) => same(k, n))) };
    }
    // "X is part of the name", "X is not a branch", "X isn't a place", "drop X", "remove X", "not X".
    const negative =
        /\b(?:part of (?:the|our|its|their) (?:business |brand |clinic )?name|(?:is|are)(?:n't| not) (?:a |an )?(?:branch|branches|outlet|outlets|place|places|location|locations)|(?:just |only )?(?:the|our) (?:business |brand )?name)\b/;
    const drops = new Set<string>();
    for (const clause of t.split(/[.;,\n]|\bbut\b|\band\b(?= \w+ (?:is|are)\b)/)) {
        if (negative.test(clause) || /\b(?:drop|remove|not)\b/.test(clause)) mentioned(clause).forEach((n) => drops.add(n));
    }
    if (!drops.size) return null;
    const keep = guess.filter((g) => !drops.has(g));
    if (!keep.length) return null;
    return { keep, drop: names.filter((n) => !keep.some((k) => same(k, n))) };
}
